
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Product, UsageRecord } from "@/lib/types";

interface UsageOverTimeChartProps {
  groupProducts: Product[];
  usageRecords: UsageRecord[];
}

const UsageOverTimeChart = ({ 
  groupProducts, 
  usageRecords 
}: UsageOverTimeChartProps) => {
  // Prepare data for usage over time
  const prepareUsageOverTimeData = () => {
    const usageByDate: Record<string, { date: string; usage: number }> = {};
    
    // Get usage records for all products in this group
    const relevantUsageRecords = usageRecords.filter(record => 
      groupProducts.some(p => p.id === record.productId)
    );
    
    // Group by date
    relevantUsageRecords.forEach(record => {
      const date = new Date(record.date);
      const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD
      const formattedDate = `${date.getMonth() + 1}/${date.getDate()}`;
      
      if (!usageByDate[dateStr]) {
        usageByDate[dateStr] = { date: formattedDate, usage: 0 };
      }
      
      usageByDate[dateStr].usage += record.quantity;
    });
    
    // Convert to array and sort by date
    return Object.values(usageByDate)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  const usageOverTimeData = prepareUsageOverTimeData();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Group Usage Over Time</CardTitle>
        <CardDescription>
          Combined usage of all products in this group over time
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={usageOverTimeData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="usage" 
                name="Group Usage" 
                stroke="#8884d8" 
                activeDot={{ r: 8 }} 
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default UsageOverTimeChart;
